import React from "react";
import { Link } from "react-router-dom";
import {
  RotateCcw,
  CheckCircle2,
  Truck,
  IndianRupee,
  Package,
} from "lucide-react";

const SEED = [
  {
    id: "RET123456",
    date: "12 May 2025",
    product: "Omega 3 Fish Oil Capsule",
    subtitle: "HealthKart · Strip of 60 Capsules",
    qty: 1,
    reason: "Received wrong item",
    refund_amount: 699,
    pickup_scheduled: "16 May 2025, 10:00 AM",
    status: "Approved",
    step: 3,
  },
  {
    id: "RET123457",
    date: "08 May 2025",
    product: "Minimalist 2% Salicylic Acid Face Wash 100 ml",
    subtitle: "Minimalist · Tube of 100ml",
    qty: 1,
    reason: "Not satisfied with the product",
    refund_amount: 399,
    pickup_scheduled: "In Progress · AWB: 12345678910",
    status: "In Transit",
    step: 4,
  },
  {
    id: "RET123458",
    date: "02 May 2025",
    product: "Accu-Chek Active Glucometer Kit",
    subtitle: "Accu-Chek · Bundle · Qty 1",
    qty: 1,
    reason: "Product defective",
    refund_amount: 1499,
    pickup_scheduled: "Refund Credited on 05 May 2025",
    status: "Refunded",
    step: 5,
  },
];

const STEPS = ["Return Requested", "Approved", "Pickup Scheduled", "Picked Up", "Refunded"];

const STATUS_COLOR = {
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
  "In Transit": "bg-[#F3EFFC] text-[#4B2AAD] border-[#4B2AAD]/20",
  Refunded: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Pending: "bg-amber-50 text-amber-700 border-amber-100",
};

export default function Returns() {
  return (
    <div data-testid="account-returns" className="space-y-6">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">
          Returns
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Track your return requests and refund status.
        </p>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={RotateCcw} label="Total Requests" value={5} sub="All return requests" />
        <Kpi icon={CheckCircle2} label="Approved" value={3} sub="Refunds approved" tone="green" />
        <Kpi icon={Truck} label="In Transit" value={1} sub="Pickup in progress" tone="purple" />
        <Kpi icon={IndianRupee} label="Total Refunds" value="₹2,785" sub="Across all returns" tone="green" />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-base font-bold text-gray-900">
            Your Return Requests
          </h3>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            Sort by:
            <select className="rounded-lg border border-gray-200 px-2 py-1 bg-white">
              <option>Newest First</option>
              <option>Oldest First</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {SEED.map((r) => (
            <div
              key={r.id}
              data-testid={`return-card-${r.id}`}
              className="rounded-2xl border border-gray-100 bg-white p-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_200px] gap-4 items-start">
                <div className="h-20 w-20 rounded-xl bg-[#F5F3FB] flex items-center justify-center">
                  <Package className="h-8 w-8 text-[#4B2AAD]/60" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-bold text-[#4B2AAD]">
                      Return ID <span className="text-gray-900">#{r.id}</span>
                    </p>
                    <span className="text-xs text-gray-500">· {r.date}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold border ${STATUS_COLOR[r.status]}`}
                    >
                      ✓ {r.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-gray-900">
                    {r.product}
                  </p>
                  <p className="text-xs text-gray-500">{r.subtitle}</p>
                  <p className="mt-2 text-xs text-gray-500">
                    <b className="text-gray-700">Reason:</b> {r.reason}
                  </p>
                  <p className="text-xs text-gray-500">
                    <b className="text-gray-700">Refund Amount:</b> ₹{r.refund_amount.toLocaleString()}
                  </p>
                </div>
                <div className="text-sm">
                  <p className="text-xs text-gray-500">
                    {r.status === "Refunded"
                      ? "Refund Credited"
                      : r.status === "In Transit"
                        ? "Pickup Progress"
                        : "Pickup Scheduled"}
                  </p>
                  <p className="text-sm font-bold text-gray-900 mt-1">
                    {r.pickup_scheduled}
                  </p>
                  <button className="mt-3 w-full rounded-xl border border-[#4B2AAD]/30 text-[#4B2AAD] hover:bg-[#F3EFFC] text-xs font-bold py-1.5">
                    View Details
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-5 gap-1">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex flex-col items-center">
                    <div
                      className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        i < r.step
                          ? "bg-emerald-500 text-white"
                          : i === r.step - 1
                            ? "bg-[#4B2AAD] text-white ring-4 ring-[#F3EFFC]"
                            : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {i < r.step ? "✓" : i + 1}
                    </div>
                    <p
                      className={`text-[10px] mt-1 text-center leading-tight ${
                        i < r.step ? "text-gray-700 font-semibold" : "text-gray-400"
                      }`}
                    >
                      {s}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-[#F3EFFC] border border-[#4B2AAD]/15 p-4 text-xs text-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <span>
          Need help with your return? Our support team is here to help with refund
          or other queries.
        </span>
        <Link to="/account/help" className="dyntra-btn-primary text-xs py-1.5">
          Contact Support
        </Link>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone }) {
  const iconBg =
    tone === "green"
      ? "bg-emerald-50 text-emerald-600"
      : tone === "red"
        ? "bg-red-50 text-red-500"
        : "bg-[#F3EFFC] text-[#4B2AAD]";
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <div className="flex items-center gap-2">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
          <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        </div>
      </div>
      <p className="text-[10px] text-gray-400 mt-2">{sub}</p>
    </div>
  );
}
